import { CalendarDays, ArrowRight, Sparkles, Clock, Palette } from "lucide-react";
import { motion } from "framer-motion";

export function Login() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/calendar";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: Clock, title: "Visual Day Timeline", desc: "See your whole day flow, hour by hour" },
    { icon: Palette, title: "Unlimited Themes", desc: "Craft any look with full color control" },
    { icon: Sparkles, title: "Drag & Resize", desc: "Reshape your schedule with a touch" },
  ];

  return (
    <div className="min-h-screen w-full flex overflow-hidden" style={{ background: "var(--bg-app)" }}>
      {/* animated ambient blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div animate={{ x: [0, 40, 0], y: [0, -30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: "var(--accent-glow)" }} />
        <motion.div animate={{ x: [0, -30, 0], y: [0, 40, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-[140px]" style={{ background: "color-mix(in srgb, var(--accent-primary) 25%, transparent)" }} />
      </div>

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-14 w-1/2 relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center accent-gradient-bg shadow-2xl"><CalendarDays size={24} className="text-white" /></div>
          <div>
            <h1 className="font-display font-extrabold text-2xl" style={{ color: "var(--text-primary)" }}>ChronoGraph</h1>
            <p className="text-xs tracking-[0.3em] uppercase" style={{ color: "var(--text-muted)" }}>Studio</p>
          </div>
        </div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-5xl font-extrabold leading-tight mb-4" style={{ color: "var(--text-primary)" }}>
            Your day,<br />beautifully <span style={{ background: "var(--accent-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>visualized</span>.
          </motion.h2>
          <p className="text-lg max-w-md mb-10" style={{ color: "var(--text-secondary)" }}>A premium visual planner where every event lives on a fluid timeline — not trapped in a tiny box.</p>
          <div className="space-y-4">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)" }}><f.icon size={18} style={{ color: "var(--accent-primary)" }} /></div>
                <div><p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{f.title}</p><p className="text-xs" style={{ color: "var(--text-muted)" }}>{f.desc}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>Crafted for PC & iPad · Precision time engineering</p>
      </div>

      {/* Right login card */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="w-full max-w-md rounded-[28px] p-8 lg:p-10 glass shadow-2xl" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center accent-gradient-bg"><CalendarDays size={22} className="text-white" /></div>
            <h1 className="font-display font-extrabold text-xl" style={{ color: "var(--text-primary)" }}>ChronoGraph</h1>
          </div>

          <h2 className="font-display text-3xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>Welcome back</h2>
          <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>Sign in to open your visual planner.</p>

          <button onClick={handleGoogle} data-testid="google-login-button"
            className="w-full h-13 py-3.5 rounded-2xl flex items-center justify-center gap-3 font-semibold transition-all hover:scale-[1.02] active:scale-[0.99] shadow-lg"
            style={{ background: "#fff", color: "#1f1f1f" }}>
            <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
            <ArrowRight size={18} className="opacity-60" />
          </button>

          <p className="text-xs text-center mt-6" style={{ color: "var(--text-muted)" }}>By continuing you agree to our Terms & Privacy Policy.</p>
        </motion.div>
      </div>
    </div>
  );
}
